import React from 'react';
import Skeleton from 'react-loading-skeleton';
import styled, { css } from 'styled-components';
import { theme, toRem } from 'design';
import { Appearance, Size } from 'design/types';
import { PayoutStatus } from 'types';
import { capitalize } from 'utils/strings';
import { Tag } from '../Tag';

export const StatusContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
  width: auto;

  > * {
    &:first-child {
      margin-right: ${toRem(7)};
    }
  }
`;

interface IStatusProps {
  appearance: Appearance;
  label: string;
  subStatusLabel?: string;
  className?: string;
  isLoading?: boolean;
  size?: Size.LARGE | Size.SMALL;
}

const DEFAULT_COLOR = theme.color.success.medium;

const Layout = styled.div<Partial<IStatusProps>>`
  flex-grow: 0;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
  width: auto;
  font-family: ${theme.font.family.euclid};
  font-weight: ${theme.font.weight.regular};
  font-size: ${toRem(13.25)};
  line-height: 100%;
  color: inherit;

  .tag {
    margin-left: ${toRem(7)};
    font-size: ${toRem(12.25)};
  }

  ${({ size }) =>
    size === 'large' &&
    css`
      @media (min-width: ${theme.breakpoint.med}px) {
        font-size: ${toRem(16.5)};
      }
    `}

  ${({ isLoading }) =>
    isLoading &&
    css`
      width: 100%;
    `}

  > span {
    display: flex;

    &:last-child {
      flex-grow: 1;
    }
  }
`;

const INDICATOR_SIZE = {
  small: 7,
  large: 11,
};
const INDICATOR_MARGIN = {
  small: 9,
  large: 7,
};

const Indicator = styled.div.attrs({ 'aria-hidden': true })<Partial<IStatusProps>>`
  position: relative;
  display: flex;
  flex-grow: 0;
  flex-shrink: 0;
  width: ${toRem(INDICATOR_SIZE.small)};
  height: ${toRem(INDICATOR_SIZE.small)};
  margin-right: ${toRem(INDICATOR_MARGIN.small)};
  border-radius: 50%;
  background-color: ${DEFAULT_COLOR};

  ${({ size }) =>
    size === 'large' &&
    css`
      margin-left: ${toRem(INDICATOR_MARGIN.small / -2)};

      @media (min-width: ${theme.breakpoint.med}px) {
        width: ${toRem(INDICATOR_SIZE.large)};
        height: ${toRem(INDICATOR_SIZE.large)};
        margin-right: ${toRem(INDICATOR_MARGIN.large)};
        margin-left: ${toRem(INDICATOR_MARGIN.large / -2)};
      }
    `}

  ${({ appearance }) =>
    appearance === Appearance.PRIMARY &&
    css`
      background-color: ${theme.color.primary[500]};
    `}


  ${({ appearance }) =>
    appearance === Appearance.WARNING &&
    css`
      background-color: ${theme.color.warning.medium};
    `}

  ${({ appearance }) =>
    appearance === Appearance.DANGER &&
    css`
      background-color: ${theme.color.danger.medium};
    `}

  ${({ appearance }) =>
    appearance === Appearance.NEUTRAL &&
    css`
      background-color: ${theme.color.neutral[400]};
    `}

  ${({ appearance }) =>
    appearance === Appearance.SUCCESS &&
    css`
      background-color: ${theme.color.success.medium};
    `}
`;

const LoadingLabel = styled(Skeleton)`
  && {
    width: 100%;
  }
`;

const LoadingIndicator = styled(Skeleton).attrs({ circle: true })<Partial<IStatusProps>>`
  && {
    width: ${toRem(INDICATOR_SIZE.small)};
    height: ${toRem(INDICATOR_SIZE.small)};
    margin-right: ${toRem(INDICATOR_MARGIN.small)};

    ${({ size }) =>
      size === 'large' &&
      css`
        @media (min-width: ${theme.breakpoint.med}px) {
          width: ${toRem(INDICATOR_SIZE.large)};
          height: ${toRem(INDICATOR_SIZE.large)};
          margin-right: ${toRem(INDICATOR_MARGIN.large)};
        }
      `}
  }
`;

export const Status: React.FC<IStatusProps> = ({ label, subStatusLabel, appearance = Appearance.SUCCESS, className, isLoading, size = Size.SMALL, ...rest }) => (
  <Layout className={className} size={size} isLoading={isLoading} {...rest}>
    {isLoading ? (
      <>
        <LoadingIndicator size={size} />
        <LoadingLabel />
      </>
    ) : (
      <>
        <Indicator size={size} appearance={appearance} />
        {label}
        {subStatusLabel && <Tag className="tag" data-cy="substatus-label" size={Size.SMALL} appearance={Appearance.WARNING} label={subStatusLabel} />}
      </>
    )}
  </Layout>
);

export interface StatusPaymentProps {
  className?: string;
  isLoading?: boolean;
  size?: Size.LARGE | Size.SMALL;
  status: PayoutStatus;
  subStatusLabel?: string;
  subStatusTooltip?: string;
  paymentId?: string;
}

export const StatusPayment = ({ className, isLoading, size = Size.SMALL, status, subStatusLabel, subStatusTooltip, paymentId, ...rest }: StatusPaymentProps) => {
  const getStatusColor = (): Appearance => {
    if (status === PayoutStatus.PAYROLL || status === PayoutStatus.CANCELED) {
      return Appearance.NEUTRAL;
    }
    if (status === PayoutStatus.EXPIRED) {
      return Appearance.DANGER;
    }
    if (status === PayoutStatus.PAID) {
      return Appearance.SUCCESS;
    }
    if (status === PayoutStatus.PENDING) {
      return Appearance.WARNING;
    }
    return Appearance.NEUTRAL;
  };

  return (
    <StatusContainer className={className}>
      <Status appearance={getStatusColor()} label={capitalize(status)} size={size} isLoading={isLoading} {...rest} />
      {!isLoading && subStatusLabel && <Tag dataCy="substatus-label" size={Size.SMALL} appearance={getStatusColor()} label={subStatusLabel} tooltipMessage={subStatusTooltip} tooltipId={`alert_${paymentId}`} />}
    </StatusContainer>
  );
};
